import { PropertyConfig, PropertyLabelLayout } from "@cervval.js/common/src/forms/propertyConfig";
import { ValueOrFunction } from "@cervval.js/common/src/tools/object.tools";
import { anyReallyAny } from "@cervval.js/common/tools";

/** 
 * Edit mode of generatedForm
 */
export enum GeneratedFormEditMode {

    /** Create new item */
    CREATE = "CREATE",

    /** Edit existing item */
    UPDATE = "UPDATE"
}

/**
 * Save mode of generatedForm
 */
export enum GeneratedFormSaveMode {

    /** Save only if user makes a save action (ex: click on "save" button) */
    STATIC = "STATIC",

    /** Auto save changes in DataModel after user update value */
    DYNAMIC = "DYNAMIC"
}

export interface GeneratedFormPropertyConfigLayoutItem {
    /** PropertyName to apply config */
    propertyName: string;

    /** Number of cols where the property will expand */
    colSpan?: number;

    /** Should add a line break after property */
    appendLineBreak?: boolean;
}

export interface GeneratedFormLayout {

    /**
     * Layout for generatedForm
     * Number of columns in form
     * @default 1
     */
    columnsCount?: number;

    /**
     * Width of labels, used in multiple columns when colSpan is given
     * (in px)
     */
    labelWidth?: number;

    /** 
     * Display form label in:
     * 
     *   INLINE (horizontal) layout:   
     *          ``
     *          {label}{editor}
     *          ``
     *
     * (or)
     * 
     *   BLOCK (vertical) layout:
     *   ``
     *     {label}
     *     {editor}
     *   ``
     */
    formLabelLayout?: PropertyLabelLayout;

    /**
     * Configs to override propertyConfig layout of given propertyConfig
     */
    propertyConfigsLayout?: GeneratedFormPropertyConfigLayoutItem[];

}

/**
 * Form mode used for GeneratedForm options
 */
export interface GeneratedFormMode {
    editMode: GeneratedFormEditMode;
    saveMode: GeneratedFormSaveMode;
}

/**
 * Options of GeneratedForm component
 */
export interface GeneratedFormOptions<DataModelType = anyReallyAny> {

    /** 
     * Mode of generatedForm
     * 
     * SaveMode:
     * DYNAMIC: Auto save after user update value
     * STATIC: Save only if user makes a save action (ex: click on "save" button) and update edited value in data model
     * 
     * EditMode:
     * CREATE: Create new item
     * UPDATE: Edit existing item
     */
    formMode: GeneratedFormMode;

    /** disable hiding the generated form if the edited data array is empty */
    disableHideOnEmptyEditedData?: boolean;

    /** Disable asterisk on required field */
    disableDisplayRequiredAsterisk?: boolean;

    /** Layout options for generatedForm */
    layout?: GeneratedFormLayout;

    /** Disable display green border for 2 seconds after editing a field */
    disableVisualReturnOnEditItemProperty?: boolean;

    /** Disable auto scroll/focus when validate a form with errors */
    disableAutoFocusOnValidationError?: boolean;

    /** Is item editable */
    isNotEditable?: (item: DataModelType) => boolean;

    /** 
     * called just before item is added to the list when creating a new item 
     * you can use this function to update item or to create multiple items
     */
    finishCreate?: (items: DataModelType[]) => DataModelType[];

    /** 
     * GeneratedForm is disabled, can't update items values
     */
    disabled?: ValueOrFunction<boolean>;
}

/** Type of detail for EditorOnValueChange event */
export type GeneratedFormEditorOnValueChangeEvent = {

    /** PropertyConfig of editor who change */
    propertyConfig: PropertyConfig,

    /** New value */
    value: unknown
}

/**
 * Return type when save a form
 */
export interface GeneratedFormSaveResult<DataModelType> {
    /** List of items to create */
    elementsToCreate: DataModelType[];

    /** List of what to update (propertyName, data, value) */
    elementsToUpdate: GeneratedFormSaveAction<DataModelType>[];
}

/** 
 * Save action interface, used to store new value to update for a dataModel item 
 */
export interface GeneratedFormSaveAction<DataModelType> {
    propertyName: keyof DataModelType;
    data: DataModelType[];
    value: anyReallyAny;
}

/** Item used to calculate the rendering of generatedForm */
export interface GeneratedFormRenderFormGridItem {
    /** Row element where item will be added in render */
    html: HTMLElement;

    /** PropertyConfig of item */
    propertyConfig: PropertyConfig;

    /** Col of item */
    col: number;

    /** Row of item */
    row: number;

    /** Should add a line-break after the propertyConfig */
    appendLineBreak: boolean;
}
